import React, { useState, useEffect } from 'react';
import { devLog, devWarn, devError } from '../../utils/devLogger';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { isShoppingMode } from '../../config/appMode';
import { useLang } from '../../context/LangContext';
import CenteredLayout from '../../layout/CenteredLayout';
import MainLayout from '../../layout/MainLayout';
import ResponsiveView from '../../layout/ResponsiveView';
import ProjectAppbar from '../../customComponents/ProjectAppbar';
import FilterChips, { ChipItem } from '../../components/common/FilterChips';
import ImageView from '../../components/ImageView';
import { FormTitle } from '../../customComponents/FormTitle';
import AuctionCard from '../../customComponents/AuctionCard';
import NoDataContainer from '../../components/NoDataContainer';
import DataLoader from '../../components/DataLoader';
import { getAuctioneerLots } from '../../lib/api/user/userApi';
import { AuctioneerLotData } from '../../lib/api/user/userApi.types';

// 웹용 제목 컴포넌트 (FormTitle과 동일한 스타일)
const WebTitle = styled.h2`
  font-size: 18px;
  font-weight: bold;
  color: #000;
  text-align: left;
  margin: 0 0 20px 0;
  grid-column: 1 / -1; /* 그리드 전체 너비를 차지 */
  
  @media (max-width: 767px) {
    display: none; /* 모바일에서는 숨김 */
  }
`;

// 컨테이너
const AuctioneerContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  min-height: calc(100vh - 120px);
`;

// 소개 섹션
const IntroSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px 16px;
  background: #f3f6f6;
  margin-bottom: 8px;

  @media (min-width: 768px) {
    padding: 32px;
    gap: 20px;
  }
`;

const IntroImage = styled.div`
  width: 140px;
  height: 140px;
  align-self: center;

  @media (min-width: 768px) {
    width: 280px;
    height: 280px;
  }
`;

const IntroDescription = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: #666;
  text-align: left;
  line-height: 1.6;
  margin: 0;

  @media (min-width: 768px) {
    font-size: 14px;
  }
`;

// 리스트 섹션
const ListSection = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  /* padding: 0 16px 24px 16px; */

  @media (min-width: 768px) {
    /* padding: 0 32px 32px 32px; */
  }
`;

// 카드 그리드
const CardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  margin-top: 24px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 32px;
  }
`;

// 에러 섹션 컴포넌트
const ErrorSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 40px 16px;
  background: #f3f6f6;
  margin-bottom: 8px;
  min-height: 200px;
  text-align: center;

  @media (min-width: 768px) {
    padding: 60px 32px;
    min-height: 280px;
  }
`;

const ErrorIcon = styled.div`
  font-size: 48px;
  color: #ccc;
  margin-bottom: 8px;

  @media (min-width: 768px) {
    font-size: 64px;
  }
`;

const ErrorTitle = styled.h3`
  font-size: 16px;
  font-weight: bold;
  color: #666;
  margin: 0;

  @media (min-width: 768px) {
    font-size: 18px;
  }
`;

const ErrorDescription = styled.p`
  font-size: 14px;
  color: #999;
  margin: 0;
  line-height: 1.5;

  @media (min-width: 768px) {
    font-size: 14px;
  }
`;

// 다국어 텍스트
const auctioneerTexts = {
  ko: {
    title: isShoppingMode() ? '판매자 소개' : '경매사 소개',
    auctionsTitle: isShoppingMode() ? '판매중인 물품' : '진행중인 경매',
    all: '전체',
    latest: '최신순',
    oldest: '과거순',
    noData: '등록된 작품이 없습니다.',
    error: '검색 결과가 없습니다.',
    errorTitle: '조회에 실패했습니다',
    errorDescription: '잠시 후 다시 시도해주세요'
  },
  en: {
    title: isShoppingMode() ? 'Seller Introduction' : 'Auctioneer Introduction',
    auctionsTitle: isShoppingMode() ? 'Products On Sale' : 'Ongoing Auctions',
    all: 'All',
    latest: 'Latest',
    oldest: 'Oldest',
    noData: 'No artworks registered.',
    error: 'No search results found.',
    errorTitle: 'Failed to load data',
    errorDescription: 'Please try again later'
  }
};

const AuctioneerPage: React.FC = () => {
  const { auctioneerIndex } = useParams<{ auctioneerIndex: string }>();
  const navigate = useNavigate();
  const { lang } = useLang();
  const texts = auctioneerTexts[lang];

  // 이미지 URL 처리 함수
  const getProfileImageSrc = (profileImageUrl?: string | null) => {
    if (!profileImageUrl || profileImageUrl.trim() === "") {
      return "/common/auctioneer-logo.jpg";
    }
    
    // 이미 완전한 URL인 경우 (http, https로 시작하거나 blob: URL)
    if (profileImageUrl.startsWith("http") || profileImageUrl.startsWith("blob:")) {
      return profileImageUrl;
    }
    
    // 절대 경로인 경우 (/로 시작)
    if (profileImageUrl.startsWith("/")) {
      return `${process.env.REACT_APP_API_HOST}${profileImageUrl}`;
    }
    
    // 단순 파일명인 경우 (서버에서 받은 파일명)
    return `${process.env.REACT_APP_API_HOST}/${profileImageUrl}`;
  };

  // 상태 관리
  const [selectedFilters, setSelectedFilters] = useState<string[]>(['all']);
  const [lots, setLots] = useState<AuctioneerLotData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page] = useState(1);
  const [auctioneerInfo, setAuctioneerInfo] = useState<{
    name: string;
    description: string;
    profilePic: string;
  } | null>(null);
  const [auctioneerInfoError, setAuctioneerInfoError] = useState(false);

  // API 데이터 로드
  useEffect(() => {
    const loadAuctioneerLots = async () => {
      if (!auctioneerIndex) return;

      setLoading(true);
      setError(null);
      setAuctioneerInfoError(false);

      try {
        const order = selectedFilters[0] === 'oldest' ? 'asc' : 'desc';
        const response = await getAuctioneerLots({
          id: auctioneerIndex,
          page,
          order
        });

        if (response.data.statusCode === 200 && response.data.data) {
          setLots(response.data.data);
          
          // 첫 번째 작품에서 경매사 정보 추출
          if (response.data.data.length > 0) {
            const firstLot = response.data.data[0];
            setAuctioneerInfo({
              name: firstLot.auctioneer.name,
              description: firstLot.auctioneer.description,
              profilePic: firstLot.auctioneer.profilePic
            });
          } else {
            // 작품이 없지만 API 호출은 성공한 경우
            setAuctioneerInfoError(true);
          }
        } else {
          setError(texts.error);
          setAuctioneerInfoError(true);
        }
      } catch (err) {
        devError('경매사 작품 조회 실패:', err);
        setError(texts.error);
        setAuctioneerInfoError(true);
      } finally {
        setLoading(false);
      }
    };

    loadAuctioneerLots();
  }, [auctioneerIndex, selectedFilters, page, texts.error]);

  // 칩 데이터
  const filterChips: ChipItem[] = [
    { key: 'all', label: texts.all },
    { key: 'latest', label: texts.latest },
    { key: 'oldest', label: texts.oldest }
  ];

  // 칩 클릭 핸들러
  const handleChipClick = (chipKey: string) => {
    setSelectedFilters([chipKey]);
  };

  // 작품을 경매 카드 형태로 변환하는 함수
  const convertLotsToAuctions = (lots: AuctioneerLotData[]) => {
    const auctionMap = new Map();
    
    lots.forEach(lot => {
      if (!auctionMap.has(lot.auction.index)) {
        auctionMap.set(lot.auction.index, {
          id: lot.auction.index,
          name: lot.auction.name,
          auctioneer: lot.auctioneer,
          preBidStartAt: lot.auction.preBidStartAt,
          preBidEndAt: lot.auction.preBidEndAt,
          auctionStartAt: lot.auction.auctionStartAt,
          auctionEndAt: lot.auction.auctionEndAt,
          lots: [],
          status: lot.ended ? 'completed' : 'ongoing'
        });
      }
      
      const auction = auctionMap.get(lot.auction.index);
      auction.lots.push({
        images: lot.images.map(img => getProfileImageSrc(img)) // 동일한 이미지 경로 처리 함수 사용
      });
    });
    
    return Array.from(auctionMap.values());
  };

  // 변환된 경매 목록
  const auctions = convertLotsToAuctions(lots);

  // 카드 클릭 핸들러
  const handleCardClick = (auctionId: number) => {
    devLog('Auction clicked:', auctionId, 'for auctioneer:', auctioneerIndex);
    
    // 해당 경매의 첫 번째 작품 찾기
    const auctionLots = lots.filter(lot => lot.auction.index === auctionId);
    if (auctionLots.length > 0) {
      // 경매 ID로 경매 소개 페이지로 이동 (getAuction API 사용)
      navigate(`/auction/${auctionId}/intro`);
    } else {
      devError('해당 경매에 작품이 없습니다.');
    }
  };

  // 뒤로가기 핸들러
  const handleBack = () => {
    navigate(-1);
  };

  // 컨텐츠 렌더링
  const renderContent = () => {
    if (error || auctions.length === 0) {
      return (
        <NoDataContainer 
          message={error || texts.noData}
          imageSize={107}
        />
      );
    }

    return (
      <CardGrid>
        {auctions.map((auction) => (
          <AuctionCard
            key={auction.id}
            auction={auction}
            lang={lang}
            type="ongoing"
            onClick={() => handleCardClick(auction.id)}
          />
        ))}
      </CardGrid>
    );
  };

  // 소개 섹션 렌더링
  const renderIntroSection = () => {
    if (auctioneerInfoError) {
      return (
        <ErrorSection>
          <ErrorIcon>📋</ErrorIcon>
          <ErrorTitle>{texts.errorTitle}</ErrorTitle>
          <ErrorDescription>{texts.errorDescription}</ErrorDescription>
        </ErrorSection>
      );
    }

    if (!auctioneerInfo) {
      return null;
    }

    return (
      <IntroSection>
        <IntroImage>
          <ImageView
            imageUrl={getProfileImageSrc(auctioneerInfo.profilePic)}
            width="100%"
            height="100%"
            borderRadius={12}
            alt={auctioneerInfo.name}
          />
        </IntroImage>
        <FormTitle>{auctioneerInfo.name}</FormTitle>
        <IntroDescription>{auctioneerInfo.description}</IntroDescription>
      </IntroSection>
    );
  };

  // 모바일 레이아웃
  const MobileLayout = (
    <MainLayout currentPage="auction">
      <CenteredLayout>
        <ProjectAppbar 
          currentPage="auction"
          title={texts.title}
          showBackButton={true}
          onBack={handleBack}
        />
        <AuctioneerContainer>
          <ContentContainer>
            {loading ? (
              <div style={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 'calc(100vh - 120px)',
                width: '100%'
              }}>
                <DataLoader />
              </div>
            ) : (
              <>
                <WebTitle>{texts.title}</WebTitle>
                {/* 소개 섹션 */}
                {renderIntroSection()}

                {/* 리스트 섹션 */}
                <ListSection>
                  <FilterChips
                    chips={filterChips}
                    selectedChips={selectedFilters}
                    onChipClick={handleChipClick}
                    allowMultiSelect={false}
                  />
                  
                  {renderContent()}
                </ListSection>
              </>
            )}
          </ContentContainer>
        </AuctioneerContainer>
      </CenteredLayout>
    </MainLayout>
  );

  // 데스크톱 레이아웃
  const DesktopLayout = (
    <MainLayout currentPage="auction">
      <CenteredLayout>
        <ProjectAppbar 
          currentPage="auction"
          title={texts.title}
          showBackButton={true}
          onBack={handleBack}
        />
        <AuctioneerContainer>
          <ContentContainer>
            {loading ? (
              <div style={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 'calc(100vh - 120px)',
                width: '100%'
              }}>
                <DataLoader />
              </div>
            ) : (
              <>
                <WebTitle>{texts.title}</WebTitle>
                {/* 소개 섹션 */}
                {renderIntroSection()}

                {/* 리스트 섹션 */}
                <ListSection>
                  <FilterChips
                    chips={filterChips}
                    selectedChips={selectedFilters}
                    onChipClick={handleChipClick}
                    allowMultiSelect={false}
                  />
                  
                  {renderContent()}
                </ListSection>
              </>
            )}
          </ContentContainer>
        </AuctioneerContainer>
      </CenteredLayout>
    </MainLayout>
  );

  return (
    <ResponsiveView
      mobileView={MobileLayout}
      desktopView={DesktopLayout}
    />
  );
};

export default AuctioneerPage;
